import { useState } from "react";
import { Loader2, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { type LessonReportRow } from "./types";
import { LessonGenerateDialog } from "./LessonGenerateDialog";

export function LessonSearchBar({
  onResult,
  onLesson,
}: {
  onResult: (r: LessonReportRow) => void;
  onLesson: (id: number) => void;
}) {
  const today = new Date().toISOString().slice(0, 10);
  const [lessonIdInput, setLessonIdInput] = useState("");
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(today);
  const [searching, setSearching] = useState(false);
  const [notFoundReason, setNotFoundReason] = useState<
    "not_found" | "no_data" | "failed" | null
  >(null);
  const [showGenerate, setShowGenerate] = useState(false);
  const [lessonIdList, setLessonIdList] = useState("");

  const resolvedLessonId = lessonIdInput ? Number(lessonIdInput) : null;

  const handleSearch = async () => {
    if (!resolvedLessonId) return;
    setSearching(true);
    setNotFoundReason(null);
    try {
      const params = new URLSearchParams({
        lessonId: String(resolvedLessonId),
        startDate,
        endDate,
      });
      const r = await fetch(
        `/api/internal/admin/lesson-reports/lookup?${params}`,
      );
      if (r.status === 404) {
        setNotFoundReason("not_found");
        return;
      }
      const data = (await r.json()) as LessonReportRow;
      if (data.status === "no_data") {
        setNotFoundReason("no_data");
        return;
      }
      if (data.status === "failed") {
        setNotFoundReason("failed");
        return;
      }
      onResult(data);
    } catch {
      setNotFoundReason("not_found");
    } finally {
      setSearching(false);
    }
  };

  return (
    <div className="rounded-xl border bg-card shadow-sm px-5 py-4 flex flex-col gap-4">
      <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
        Look up report
      </p>

      <div className="flex flex-col gap-3">
        <div className="flex flex-wrap items-end gap-2">
          <div className="flex flex-col gap-1 w-28">
            <label className="text-xs text-muted-foreground">Lesson ID</label>
            <Input
              placeholder="e.g. 5078"
              value={lessonIdInput}
              onChange={(e) => {
                if (/^\d*$/.test(e.target.value)) {
                  setLessonIdInput(e.target.value);
                  setNotFoundReason(null);
                }
              }}
              onKeyDown={(e) => e.key === "Enter" && handleSearch()}
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs text-muted-foreground">Start Date</label>
            <Input
              type="date"
              value={startDate}
              onChange={(e) => {
                setStartDate(e.target.value);
                setNotFoundReason(null);
              }}
              className="w-40"
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs text-muted-foreground">End Date</label>
            <Input
              type="date"
              value={endDate}
              onChange={(e) => {
                setEndDate(e.target.value);
                setNotFoundReason(null);
              }}
              className="w-40"
            />
          </div>
          <Button
            onClick={handleSearch}
            disabled={searching || !resolvedLessonId}
            className="gap-2"
          >
            {searching ? (
              <Loader2 className="size-4 animate-spin" />
            ) : (
              <Search className="size-4" />
            )}
            Search
          </Button>
        </div>
        {notFoundReason && (
          <div className="flex items-center gap-3">
            <p className="text-xs text-muted-foreground">
              {notFoundReason === "no_data"
                ? `No sessions found for this date range when last checked. Want to try again?`
                : notFoundReason === "failed"
                  ? `Report generation failed for this date range. Want to try again?`
                  : `No report found for this date range.`}
            </p>
            <Button
              size="sm"
              variant="outline"
              className="text-xs h-7"
              onClick={() => setShowGenerate(true)}
            >
              {notFoundReason === "no_data" || notFoundReason === "failed"
                ? "Regenerate"
                : "Generate"}
            </Button>
          </div>
        )}
        {showGenerate && resolvedLessonId && (
          <LessonGenerateDialog
            lessonId={resolvedLessonId}
            startDate={startDate}
            endDate={endDate}
            onClose={() => setShowGenerate(false)}
            onDone={(r) => {
              setShowGenerate(false);
              onResult(r);
            }}
          />
        )}
      </div>

      <div className="border-t" />

      <div className="flex flex-wrap items-end gap-2">
        <div className="flex flex-col gap-1 w-28">
          <label className="text-xs text-muted-foreground">Lesson ID</label>
          <Input
            placeholder="e.g. 5078"
            value={lessonIdList}
            onChange={(e) => {
              if (/^\d*$/.test(e.target.value)) setLessonIdList(e.target.value);
            }}
            onKeyDown={(e) =>
              e.key === "Enter" &&
              lessonIdList &&
              onLesson(Number(lessonIdList))
            }
          />
        </div>
        <Button
          onClick={() => lessonIdList && onLesson(Number(lessonIdList))}
          disabled={!lessonIdList}
          variant="outline"
          className="gap-2"
        >
          <Search className="size-4" />
          Show all reports
        </Button>
      </div>
    </div>
  );
}
